import React from 'react';
import { keyframes } from '@mui/system';
import TypewriterText from '../components/TypewriterText';
import heroBg from '../assets/images/hpe_servers/HPE_ProLiant_ML110_Gen11_server.png';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
  Chip,
} from '@mui/material';
import {
  Verified as VerifiedIcon,
  Support as SupportIcon,
  CloudDone as CloudIcon,
} from '@mui/icons-material';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Hero: React.FC = () => {
  const handleNavigation = (section: string) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const trustIndicators = [
    { icon: <VerifiedIcon />, label: 'Enterprise Certified Partner' },
    { icon: <SupportIcon />, label: 'Expert Support' },
    { icon: <CloudIcon />, label: 'Consumption-based as-a-Service' },
  ];

  return (
    <Box
      id="home"
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        py: { xs: 10, md: 16 },
        mt: '-64px',
        pt: { xs: 12, md: 18 },
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'none',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              textAlign: { xs: 'center', md: 'left' }, 
              position: 'relative', 
              zIndex: 2,
              animation: `${fadeInLeft} 0.8s ease-out`,
            }}>
              <TypewriterText
                text="TNCOSOFT"
                variant="h1"
                component="h1"
                delay={1000}
                speed={150}
                sx={{ 
                  color: 'text.primary', 
                  mb: 2,
                  fontWeight: 800,
                }}
                role="heading"
                aria-level={1}
              />
              <TypewriterText
                text="Enterprise Infrastructure Solutions"
                variant="h3"
                component="h2"
                delay={2500}
                speed={80}
                sx={{ 
                  color: 'text.primary', 
                  mb: 2,
                  fontWeight: 300,
                }}
              />
              
              <Typography
                variant="h6"
                paragraph
                sx={{ 
                  mb: 4, 
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  animation: `${fadeInUp} 0.8s ease-out 0.6s both`,
                }}
                role="text"
                aria-describedby="hero-description"
              >
                <span id="hero-description">
                  Delivering enterprise-grade virtualization, private cloud, and data protection with flexible 
                  consumption-based models. Scale your infrastructure with pay-per-use solutions 
                  that combine cloud agility with on-premises security and control.
                </span>
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ 
                  mb: 4,
                  animation: `${fadeInUp} 0.8s ease-out 0.8s both`,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleNavigation('contact')}
                  sx={{ 
                    py: 1.5, 
                    px: 4,
                  }}
                  aria-label="Get a quote for your IT infrastructure"
                >
                  Get a Quote
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => handleNavigation('services')}
                  sx={{ 
                    py: 1.5, 
                    px: 4,
                  }}
                  aria-label="Explore our infrastructure solutions"
                >
                  Explore Our Solutions
                </Button>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                sx={{ 
                  gap: 1,
                  animation: `${fadeInUp} 0.8s ease-out 1.0s both`,
                }}
              >
                {trustIndicators.map((indicator, index) => (
                  <Chip
                    key={index}
                    icon={indicator.icon}
                    label={indicator.label}
                    variant="outlined"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '& .MuiChip-icon': { color: 'primary.main' },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                animation: `${fadeInRight} 0.8s ease-out 0.4s both`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              role="img"
              aria-label="NVIDIA GB200 NVL4 AI and HPC infrastructure"
            >
              <Box
                component="img"
                src={heroBg}
                alt="NVIDIA GB200 NVL4 - Next-generation AI infrastructure"
                loading="lazy"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;